/**
 * Sprezza Meals - Subscription Redirect Module v3
 *
 * Intercepts add-to-cart for subscription products and redirects
 * to meal selection page instead of showing cart drawer.
 *
 * This version uses event delegation to work with:
 * - Regular product pages
 * - Quick Add modals (dynamically added to DOM)
 * - Any other dynamically rendered subscription forms
 *
 * Flow:
 * 1. Customer clicks Add to Cart on subscription (10/15/20-Meal Plan)
 * 2. This script detects the click via event delegation
 * 3. After cart update, redirects to /pages/meal-selection?tier=N
 */

(function() {
  'use strict';

  // Configuration
  const MEAL_SELECTION_PATH = '/pages/meal-selection';
  const REDIRECT_TIMEOUT = 3000; // Fallback timeout in ms
  const VALID_TIERS = [10, 15, 20];

  // State
  let pendingRedirect = null;
  let redirectTimeoutId = null;
  let isInitialized = false;

  /**
   * Check if currently on the meal selection page
   * Prevents redirect loops when user is selecting meals
   */
  function isOnMealSelectionPage() {
    return window.location.pathname.includes(MEAL_SELECTION_PATH);
  }

  /**
   * Extract subscription data from a buy-buttons element
   */
  function extractSubscriptionData(buyButtonsElement) {
    if (!buyButtonsElement) return null;
    if (buyButtonsElement.dataset.subscription !== 'true') return null;

    const tier = parseInt(buyButtonsElement.dataset.subscriptionTier, 10);
    if (!tier || !VALID_TIERS.includes(tier)) return null;

    return { tier };
  }

  /**
   * Find the closest subscription element from a click target
   * Supports both product-buy-buttons-element and product-quick-add elements
   */
  function findSubscriptionElement(target) {
    // Look for buy-buttons element with subscription data
    const buyButtons = target.closest('product-buy-buttons-element[data-subscription="true"]');
    if (buyButtons) return buyButtons;

    // Look for quick-add element with subscription data
    const quickAdd = target.closest('product-quick-add[data-subscription="true"]');
    if (quickAdd) return quickAdd;

    // Also check if we're inside a form that's inside a subscription element
    const form = target.closest('form');
    if (form) {
      const parentBuyButtons = form.closest('product-buy-buttons-element[data-subscription="true"]');
      if (parentBuyButtons) return parentBuyButtons;
    }

    return null;
  }

  /**
   * Perform the redirect to meal selection
   */
  function performRedirect(tier) {
    // Skip redirect if already on meal selection page (prevents loops)
    if (isOnMealSelectionPage()) {
      console.log('Sprezza: Already on meal selection page, skipping redirect');
      clearTimeout(redirectTimeoutId);
      pendingRedirect = null;
      return;
    }

    // Clear any pending state
    clearTimeout(redirectTimeoutId);
    pendingRedirect = null;

    // Redirect to meal selection with tier
    const url = `${MEAL_SELECTION_PATH}?tier=${tier}`;
    console.log(`Sprezza: Redirecting to ${url}`);
    window.location.href = url;
  }

  /**
   * Add subscription to cart via API
   * Used for Quick Add where Recharge widget isn't available
   */
  async function addSubscriptionToCart(productHandle, tier) {
    try {
      // Fetch product data to get variant and selling plan
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product fetch failed');

      const product = await response.json();

      // Get the first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (!variant) throw new Error('No variant found');

      // Find the first selling plan (subscription)
      let sellingPlanId = null;
      if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
        const group = product.selling_plan_groups[0];
        if (group.selling_plans && group.selling_plans.length > 0) {
          sellingPlanId = group.selling_plans[0].id;
        }
      }

      if (!sellingPlanId) {
        console.warn('Sprezza: No selling plan found, adding without subscription');
      }

      // Build cart add payload
      const payload = {
        id: variant.id,
        quantity: 1
      };

      if (sellingPlanId) {
        payload.selling_plan = sellingPlanId;
      }

      // Add to cart
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!addResponse.ok) throw new Error('Cart add failed');

      console.log('Sprezza: Subscription added to cart successfully');
      return true;

    } catch (error) {
      console.error('Sprezza: Failed to add subscription to cart', error);
      return false;
    }
  }

  /**
   * Set up global click listener using event delegation
   * This catches clicks on ANY subscription buy button, including Quick Add modals
   */
  function setupGlobalClickListener() {
    document.addEventListener('click', async (e) => {
      // Check if clicked on a submit button, Add to Cart button, or Quick Add button
      const submitBtn = e.target.closest('button[type="submit"], .product-buy-buttons--primary, [data-action="add-to-cart"], .product-quick-add--button');
      if (!submitBtn) return;

      // Find the subscription element
      const buyButtonsElement = findSubscriptionElement(e.target);
      if (!buyButtonsElement) return;

      const subscriptionData = extractSubscriptionData(buyButtonsElement);
      if (!subscriptionData) return;

      console.log(`Sprezza: Subscription add-to-cart detected (tier ${subscriptionData.tier})`);

      // Check if this is a Quick Add click (product-quick-add element)
      const quickAddElement = e.target.closest('product-quick-add[data-subscription="true"]');
      if (quickAddElement) {
        // For Quick Add, we need to handle the cart add ourselves
        // because the Quick Add form doesn't include the Recharge widget
        e.preventDefault();
        e.stopPropagation();

        const productHandle = quickAddElement.dataset.handle;
        console.log(`Sprezza: Quick Add detected for ${productHandle}, adding subscription manually`);

        // Show loading state on the button
        quickAddElement.setAttribute('aria-busy', 'true');

        // Add subscription to cart
        const success = await addSubscriptionToCart(productHandle, subscriptionData.tier);

        if (success) {
          // Redirect to meal selection
          performRedirect(subscriptionData.tier);
        } else {
          // Reset loading state on failure
          quickAddElement.setAttribute('aria-busy', 'false');
          console.error('Sprezza: Quick Add subscription failed');
        }

        return; // Don't continue with normal flow
      }

      // For regular Add to Cart (product page), use the normal flow
      // Set pending redirect flag
      pendingRedirect = {
        tier: subscriptionData.tier,
        timestamp: Date.now()
      };

      // Set up fallback timeout
      clearTimeout(redirectTimeoutId);
      redirectTimeoutId = setTimeout(() => {
        if (pendingRedirect) {
          console.log('Sprezza: Fallback redirect triggered');
          checkCartAndRedirect(pendingRedirect.tier);
        }
      }, REDIRECT_TIMEOUT);
    }, true); // Use capture phase to catch events early
  }

  /**
   * Set up observer for cart drawer opening
   */
  function setupCartDrawerObserver() {
    // Common selectors for cart drawers in Shopify themes
    const drawerSelectors = [
      '[data-cart-drawer]',
      '.cart-drawer',
      '[data-section-type="cart-drawer"]',
      '#cart-drawer',
      '.drawer[data-drawer="cart"]',
      '[data-drawer="cart"]'
    ];

    let cartDrawer = null;
    for (const selector of drawerSelectors) {
      cartDrawer = document.querySelector(selector);
      if (cartDrawer) break;
    }

    if (cartDrawer) {
      console.log('Sprezza: Cart drawer found, setting up observer');
      observeElement(cartDrawer);
    }

    // Also observe body for cart drawer class changes
    observeBodyForCartDrawer();
  }

  /**
   * Observe a specific element for visibility changes
   */
  function observeElement(element) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue;

        // Check for visibility changes
        const isVisible =
          element.getAttribute('aria-hidden') === 'false' ||
          element.classList.contains('is-visible') ||
          element.classList.contains('active') ||
          element.classList.contains('open') ||
          element.classList.contains('is-open');

        if (isVisible && pendingRedirect) {
          // Verify redirect is recent (within last 5 seconds)
          if (Date.now() - pendingRedirect.timestamp < 5000) {
            console.log('Sprezza: Cart drawer opened, redirecting');
            setTimeout(() => performRedirect(pendingRedirect.tier), 150);
          } else {
            pendingRedirect = null;
          }
        }
      }
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'style']
    });
  }

  /**
   * Observe body for cart drawer state classes
   */
  function observeBodyForCartDrawer() {
    const observer = new MutationObserver((mutations) => {
      if (!pendingRedirect) return;
      if (Date.now() - pendingRedirect.timestamp > 5000) {
        pendingRedirect = null;
        return;
      }

      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target === document.body) {
          if (document.body.classList.contains('cart-drawer-open') ||
              document.body.classList.contains('drawer-open') ||
              document.body.classList.contains('js-drawer-open')) {
            console.log('Sprezza: Body cart class detected, redirecting');
            setTimeout(() => performRedirect(pendingRedirect.tier), 150);
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Intercept fetch requests as a backup detection method
   */
  function setupFetchInterceptor() {
    const originalFetch = window.fetch;

    window.fetch = async function(url, options) {
      const response = await originalFetch.apply(this, arguments);

      // Check if this is a successful cart add request
      if (typeof url === 'string' && url.includes('/cart/add') && pendingRedirect) {
        try {
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();

          // If add was successful
          if (data && (data.items || data.id || data.key)) {
            console.log('Sprezza: Cart add successful via fetch, waiting for drawer');
            // The drawer observer will handle the redirect
          }
        } catch (e) {
          // JSON parse failed, ignore
        }
      }

      return response;
    };
  }

  /**
   * Fallback: Check cart state via API and redirect if subscription found
   */
  async function checkCartAndRedirect(tier) {
    // Skip if already on meal selection page (prevents loops)
    if (isOnMealSelectionPage()) {
      console.log('Sprezza: Already on meal selection page, skipping cart check');
      pendingRedirect = null;
      return;
    }

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check if any item has a selling_plan (subscription indicator)
      const hasSubscription = cart.items && cart.items.some(item =>
        item.selling_plan_allocation ||
        (item.properties && item.properties._subscription)
      );

      if (hasSubscription) {
        console.log('Sprezza: Subscription found in cart via API');
        performRedirect(tier);
      } else {
        console.log('Sprezza: No subscription in cart, redirect cancelled');
        pendingRedirect = null;
      }
    } catch (e) {
      console.warn('Sprezza: Could not verify cart state', e);
      pendingRedirect = null;
    }
  }

  /**
   * Initialize the redirect module
   * This runs on EVERY page to catch Quick Add modals
   */
  function init() {
    if (isInitialized) return;
    isInitialized = true;

    // Always set up listeners - don't check for subscription elements
    // This allows us to catch Quick Add modals that are added dynamically
    setupGlobalClickListener();
    setupCartDrawerObserver();
    setupFetchInterceptor();

    console.log('Sprezza: Subscription redirect v3 initialized (global event delegation)');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
